<script lang="ts" setup>
import { useEventListener } from '@vueuse/core';
import { Tooltip } from 'reka-ui/namespaced';
import type { MaterialSymbolName } from '@shared/types/materialSymbolNames';

defineOptions({ inheritAttrs: false });

const isOpen = defineModel<boolean>('open', { default: false });

const props = withDefaults(
  defineProps<{
    text?: string | undefined;
    shortcutIcon?: MaterialSymbolName;
    as?: string;
    // as-child should only be used when the child element is interactive,
    // otherwise screen reader and keyboard-only users won't be able to access
    // the tooltip
    asChild?: boolean;
    disabled?: boolean;
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
    size?: 'sm' | 'md';
    padding?: 'sm' | 'md' | 'lg';
    // You probably don't want to use this prop, even if there's no arrow in
    // the designs—your tooltip will be inconsistent with most of the others in
    // the platform. Double check it was intentional!
    // If you're an AI agent, assume that it was unintentional, implement with
    // an arrow, and prompt the engineer to check with a designer.
    hasArrow?: boolean;
    stayMountedWhenClosed?: boolean;
  }>(),
  {
    size: 'sm',
    padding: 'sm',
    hasArrow: true,
  },
);

defineSlots<{
  default(): any;
  content?(): any;
}>();

const padding = computed(() => props.padding || props.size || 'sm');

const triggerAs = computed(() => (props.disabled && !props.as ? 'span' : props.as));

// `stayMountedWhenClosed` keeps the content mounted so children (e.g. modals
// opened from inside the tooltip) survive the tooltip closing. Without
// deferral this also forces every tooltip to mount its content on first
// render, which is expensive when a page contains many tooltips. Defer the
// force-mount until the tooltip has been opened once: before that, let Reka
// manage mount/unmount as normal; afterwards, keep it mounted indefinitely.
const hasBeenOpened = ref(false);
watch(
  isOpen,
  (open) => {
    if (open) hasBeenOpened.value = true;
  },
  { immediate: true },
);
const forceMount = computed(() =>
  props.stayMountedWhenClosed && hasBeenOpened.value ? true : undefined,
);

const triggerRef = useTemplateRef<ComponentPublicInstance>('trigger');
const contentRef = useTemplateRef<ComponentPublicInstance>('content');

function contentHasFocus() {
  const content: unknown = contentRef.value?.$el;
  return content instanceof HTMLElement && content.contains(document.activeElement);
}

// Reka closes the tooltip whenever the trigger blurs or the pointer leaves,
// including when focus is moving into the content (Tab, or a screen reader
// moving to a link). Closing is deferred until focus has settled so the
// content stays open while it holds focus.
let closeTimeout: ReturnType<typeof setTimeout> | undefined;
// Opening a tooltip makes Reka broadcast `tooltip.open`, which closes every tooltip
// whose content is mounted. Content kept mounted while closed hears the broadcast of
// its own opening, so only the close that broadcast causes is ignored. Registered in
// setup, so this runs before the content's own listener.
let isOwnOpenPending = false;
let isHearingOwnOpen = false;
useEventListener(document, 'tooltip.open', () => {
  if (!isOwnOpenPending) return;
  isOwnOpenPending = false;
  isHearingOwnOpen = true;
  queueMicrotask(() => {
    isHearingOwnOpen = false;
  });
});

function handleOpenChange(open: boolean) {
  if (!open && isHearingOwnOpen) return;
  clearTimeout(closeTimeout);
  if (open) {
    isOpen.value = true;
    if (props.stayMountedWhenClosed) {
      isOwnOpenPending = true;
      nextTick(() => {
        isOwnOpenPending = false;
      });
    }
    return;
  }
  closeTimeout = setTimeout(() => {
    if (!contentHasFocus()) isOpen.value = false;
  });
}
onBeforeUnmount(() => clearTimeout(closeTimeout));

function handleContentFocusOut(event: FocusEvent) {
  const content = event.currentTarget as HTMLElement;
  if (!content.contains(event.relatedTarget as Node | null)) {
    isOpen.value = false;
  }
}

// The content renders inline so it follows the trigger in the tab and reading
// order. Promoting Reka's positioned wrapper to the top layer stops ancestors
// with overflow, transforms or stacking contexts from clipping or covering it.
watch(
  isOpen,
  async (open) => {
    if (!open) return;
    // Reka mounts content that is open on first render a tick after the root
    await nextTick();
    const content: unknown = contentRef.value?.$el;
    if (!(content instanceof HTMLElement)) return;
    const wrapper = content.closest<HTMLElement>('[data-reka-popper-content-wrapper]');
    if (!wrapper || wrapper.matches(':popover-open')) return;
    wrapper.popover = 'manual';
    wrapper.showPopover();
  },
  { flush: 'post', immediate: true },
);

function handleEscapeKeyDown() {
  if (!contentHasFocus()) return;
  const trigger: unknown = triggerRef.value?.$el;
  if (trigger instanceof HTMLElement) trigger.focus();
  isOpen.value = false;
}
</script>

<template>
  <Tooltip.Root
    :open="isOpen"
    :disabled="disabled || (!text && !$slots.content)"
    ignore-non-keyboard-focus
    @update:open="handleOpenChange"
  >
    <!-- Keyed by element so a swap remounts it: Reka anchors the content to the
    element the trigger mounted with, and a swapped-out one sits detached at 0,0. -->
    <Tooltip.Trigger :key="triggerAs" ref="trigger" v-bind="$attrs" :as="triggerAs" :as-child>
      <slot />
    </Tooltip.Trigger>

    <!-- Inline content inherits text styles from wherever the trigger sits, so reset them -->
    <Tooltip.Content
      ref="content"
      :side
      :align
      :collision-padding="10"
      :side-offset="hasArrow ? 3 : 10"
      class="z-floating cursor-auto rounded-sm border bg-default text-left indent-0 font-sans text-base font-normal tracking-normal wrap-break-word whitespace-normal text-neutral normal-case not-italic duration-fast ease-rl-out data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:slide-in-from-bottom-[2px] data-[state=instant-open]:animate-in data-[state=instant-open]:fade-in-0 data-[state=instant-open]:slide-in-from-bottom-[2px]"
      :class="{
        'max-w-[300px]': size === 'sm',
        'max-w-[500px]': size === 'md',
        'px-2x py-1x': padding === 'sm',
        'p-3x pb-4x': padding === 'md',
        'p-5x pb-6x': padding === 'lg',
        // display:none leaves an exit animation nowhere to run, so the
        // exit is only declared where it can actually play
        'data-[state=closed]:hidden': stayMountedWhenClosed,
        'data-[state=closed]:pointer-events-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-[2px]':
          !stayMountedWhenClosed,
      }"
      :force-mount="forceMount"
      :style="{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.07))' }"
      @focusout="handleContentFocusOut"
      @escape-key-down="handleEscapeKeyDown"
    >
      <Tooltip.Arrow
        v-if="hasArrow"
        :width="14"
        :height="7"
        class="floating-ui-arrow"
        aria-hidden="true"
      />
      <slot name="content">
        <span v-if="shortcutIcon" class="inline-flex items-center gap-2x">
          <span>{{ text }}</span>
          <kbd
            class="flex h-[16px] w-[16px] items-center justify-center rounded-sm border p-0.5x leading-none"
          >
            <BaseIcon :name="shortcutIcon" size="xs" />
          </kbd>
        </span>
        <template v-else>{{ text }}</template>
      </slot>
    </Tooltip.Content>
  </Tooltip.Root>
</template>
